import { Duration } from "luxon";
import { autorun, type IReactionDisposer } from "mobx";

import type { Uuid } from "@/domains/global/identifiers";
import { logger } from "@/modules/logger";
import { IntervalObservable } from "@/store/common";
import type { AppStoreAudioUiStore } from "@/store/audio/ui/audio-ui.store";

type AutoStopManagerDeps = {
  uiStore: AppStoreAudioUiStore;
  stopRecording: (args: { audioRecordingId: Uuid }) => Promise<void>;
  getPersistedDurationMs: (audioRecordingId: Uuid) => Promise<number>;
  isRecordingActive: (audioRecordingId: Uuid) => boolean;
};

export const AUDIO_RECORDING_AUTO_STOP_LIMIT_DURATION = Duration.fromObject({ hours: 2 });
const AUDIO_RECORDING_AUTO_STOP_POLL_DURATION = Duration.fromObject({ seconds: 5 });

export class AudioRecordingAutoStopManager {
  private readonly uiStore: AppStoreAudioUiStore;
  private readonly stopRecording: AutoStopManagerDeps["stopRecording"];
  private readonly getPersistedDurationMs: AutoStopManagerDeps["getPersistedDurationMs"];
  private readonly isRecordingActive: AutoStopManagerDeps["isRecordingActive"];
  private readonly limitMs: number = AUDIO_RECORDING_AUTO_STOP_LIMIT_DURATION.as("milliseconds");
  private readonly pollDuration: Duration = AUDIO_RECORDING_AUTO_STOP_POLL_DURATION;

  private trackedRecordingId: Uuid | null = null;
  private interval: IntervalObservable | null = null;
  private intervalDisposer: IReactionDisposer | null = null;
  private elapsedMsBase = 0;
  private elapsedMsSinceStart = 0;
  private hasTriggered = false;

  constructor({
    uiStore,
    stopRecording,
    getPersistedDurationMs,
    isRecordingActive,
  }: AutoStopManagerDeps) {
    this.uiStore = uiStore;
    this.stopRecording = stopRecording;
    this.getPersistedDurationMs = getPersistedDurationMs;
    this.isRecordingActive = isRecordingActive;
  }

  async beginMonitoring({ audioRecordingId }: { audioRecordingId: Uuid }): Promise<void> {
    if (this.trackedRecordingId === audioRecordingId) {
      return;
    }

    this.teardown();

    this.trackedRecordingId = audioRecordingId;
    this.elapsedMsSinceStart = 0;
    this.hasTriggered = false;

    try {
      this.elapsedMsBase = await this.getPersistedDurationMs(audioRecordingId);
    } catch (error) {
      logger.warn({
        message: "[AudioRecordingAutoStopManager] failed to resolve initial duration",
        info: { error: error instanceof Error ? error.message : String(error) },
      });
      this.elapsedMsBase = 0;
    }

    this.interval = new IntervalObservable({ duration: this.pollDuration });
    this.intervalDisposer = autorun(() => {
      // react to interval ticks
      void this.interval?.value;
      this.checkForLimit();
    });

    this.checkForLimit();
  }

  endMonitoring({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    if (this.trackedRecordingId !== audioRecordingId) {
      return;
    }

    this.teardown();
  }

  reset(): void {
    this.teardown();
  }

  isTracking(audioRecordingId: Uuid): boolean {
    return this.trackedRecordingId === audioRecordingId;
  }

  handleChunk({
    audioRecordingId,
    durationMs,
  }: {
    audioRecordingId: Uuid;
    durationMs: number;
  }): void {
    if (
      !this.trackedRecordingId ||
      this.trackedRecordingId !== audioRecordingId ||
      this.hasTriggered
    ) {
      return;
    }

    this.elapsedMsSinceStart += durationMs;
    this.checkForLimit();
  }

  handleControllerStop({ audioRecordingId }: { audioRecordingId: Uuid }): void {
    if (this.trackedRecordingId !== audioRecordingId) {
      return;
    }

    this.teardown();
  }

  private checkForLimit(): void {
    if (!this.trackedRecordingId || this.hasTriggered) {
      return;
    }

    if (!this.isRecordingActive(this.trackedRecordingId)) {
      return;
    }

    const totalElapsed = this.elapsedMsBase + this.elapsedMsSinceStart;
    if (totalElapsed < this.limitMs) {
      return;
    }

    this.triggerAutoStop();
  }

  private triggerAutoStop(): void {
    if (!this.trackedRecordingId || this.hasTriggered) {
      return;
    }

    this.hasTriggered = true;
    this.uiStore.setAutoStopModalVisible(true);

    const audioRecordingId = this.trackedRecordingId;
    void this.stopRecording({ audioRecordingId }).catch(error => {
      logger.warn({
        message: "[AudioRecordingAutoStopManager] stop recording failed",
        info: { audioRecordingId, error: error instanceof Error ? error.message : String(error) },
      });
    });
  }

  private teardown(): void {
    this.intervalDisposer?.();
    this.intervalDisposer = null;
    this.interval?.dispose();
    this.interval = null;
    this.trackedRecordingId = null;
    this.elapsedMsBase = 0;
    this.elapsedMsSinceStart = 0;
    this.hasTriggered = false;
  }
}
